// Utilisateur connecte et son role (RoleController cote serveur) : le COACH
// definit les seances et etablit le calendrier (le sien et celui de ses
// athletes rattaches) ; l'ATHLETE consulte son calendrier.
import * as React from 'react';
import { fuseauNavigateur } from '../Planning/dates.ts';

export type AthleteRattache = {
  // Identifiant du rattachement.
  id: string;
  email: string;
  // Connus une fois que l'athlete s'est connecte.
  nom?: string | null;
  ssoUserId?: string | null;
  // Photo du compte Google (URL), connue une fois l'athlete reconnecte.
  photo?: string | null;
  icuConfigure: boolean;
  // Fuseau horaire de l'athlete : le jour courant de son calendrier y est calcule.
  fuseau?: string | null;
  // Faux tant que l'athlete ne s'est pas reconnecte (fuseau par defaut).
  fuseauRenseigne?: boolean;
  // Distance de course preparee (cf Construction/distanceCourse.ts), fixee par le coach.
  distancePreparee?: string | null;
  // Invitation pas encore acceptee par l'athlete : pas d'acces a son calendrier.
  enAttente: boolean;
};

// Invitation d'un coach en attente de reponse de l'athlete.
export type InvitationCoach = {
  // Identifiant du rattachement.
  id: string;
  // Nom ou email du coach.
  coach?: string | null;
  dateCreation?: string | null;
};

export type Role = 'COACH' | 'ATHLETE';

export type Moi = {
  email?: string | null;
  nom?: string | null;
  // Null : profil pas encore choisi (premiere connexion, cf ChoixProfil).
  role: Role | null;
  // Administrateur de l'application (ecran Utilisateurs, imports de courses).
  admin?: boolean;
  // Fuseau horaire memorise (null : pas encore connu, cf chargerMoi).
  fuseau?: string | null;
  // Distance de course preparee, fixee par le coach (l'athlete la consulte).
  distancePreparee?: string | null;
  // Athlete : son coach.
  coach?: string | null;
  // Coach : ses athletes (et invitations envoyees).
  athletes: AthleteRattache[];
  // Invitations de coachs recues.
  invitations: InvitationCoach[];
  // Club de l'utilisateur (null s'il n'en a pas) et administration de ce club.
  idClub?: string | null;
  adminClub?: boolean;
  // Administrateur de son club sans y entrainer : ni calendrier, ni journal, ni seances.
  administrateurSeul?: boolean;
  // Coach : invitations a rejoindre un club.
  invitationsClub?: InvitationClub[];
};

export const MOI_MODIFIE_EVENT = 'app:moi-modifie';
const CLE_CACHE = 'moi';

function entetes(): HeadersInit {
  return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + sessionStorage.getItem('token') };
}

async function lire<T>(r: Response): Promise<T> {
  if (r.status === 200) return (await r.json()) as T;
  let message = 'statut ' + r.status;
  try {
    const erreur = await r.json();
    if (erreur?.message) message = erreur.message;
  } catch {
    // corps vide
  }
  throw new Error(message);
}

// Cache de session, lie a l'utilisateur connecte (un autre compte ne recupere
// pas le role du precedent). Pas au jeton : il est renouvele a chaque appel.
function utilisateurCourant(): string | null {
  return sessionStorage.getItem('user');
}

function lireCache(): Moi | null {
  try {
    const brut = sessionStorage.getItem(CLE_CACHE);
    if (!brut) return null;
    const { utilisateur, moi } = JSON.parse(brut) as { utilisateur: string; moi: Moi };
    return utilisateur === utilisateurCourant() ? moi : null;
  } catch {
    return null;
  }
}

function ecrireCache(moi: Moi) {
  try {
    sessionStorage.setItem(CLE_CACHE, JSON.stringify({ utilisateur: utilisateurCourant(), moi }));
  } catch {
    // stockage indisponible
  }
}

// Detection du fuseau horaire, au plus une fois par chargement de l'application.
let detectionFuseauFaite = false;

// Chargement en cours : chaque composant qui utilise useMoi le demande au
// montage ; une seule requete /api/moi est envoyee pour tous. Apres une
// modification (MOI_MODIFIE_EVENT), une nouvelle requete est envoyee.
let chargementEnCours: Promise<Moi> | null = null;

// Enregistre avant les ecouteurs des composants : ils rechargent apres.
if (typeof window !== 'undefined') {
  window.addEventListener(MOI_MODIFIE_EVENT, () => { chargementEnCours = null; });
}

/**
 * Nouvelle connexion (ou deconnexion) : oublie le chargement en cours. Un
 * appel parti avec un jeton expire ne repond jamais (cf httpInterceptor) ;
 * sans cela, les composants de la nouvelle session l'attendraient et le
 * menu resterait sans role jusqu'au rafraichissement de la page.
 */
export function oublierChargementMoi() {
  chargementEnCours = null;
}

export function chargerMoi(): Promise<Moi> {
  if (!chargementEnCours) {
    const chargement: Promise<Moi> = chargerMoiServeur().finally(() => {
      if (chargementEnCours === chargement) chargementEnCours = null;
    });
    chargementEnCours = chargement;
  }
  return chargementEnCours;
}

async function chargerMoiServeur(): Promise<Moi> {
  const moi = await lire<Moi>(await fetch('/api/moi', { headers: entetes() }));
  // Fuseau horaire pas encore connu : celui du navigateur (modifiable dans
  // les preferences). Il fixe le jour courant du calendrier, y compris
  // quand le coach le modifie depuis un autre fuseau.
  if (!moi.fuseau && !detectionFuseauFaite) {
    detectionFuseauFaite = true;
    try {
      moi.fuseau = await ecrireFuseau(fuseauNavigateur());
    } catch {
      // reessaye au prochain chargement
    }
  }
  ecrireCache(moi);
  return moi;
}

async function ecrireFuseau(fuseau: string): Promise<string> {
  const r = await lire<{ fuseau: string }>(
    await fetch('/api/preference/fuseau', { method: 'PUT', headers: entetes(), body: JSON.stringify({ fuseau }) }),
  );
  return r.fuseau;
}

/** Enregistre le fuseau horaire de l'utilisateur (ex Europe/Paris). */
export async function enregistrerFuseau(fuseau: string): Promise<string> {
  const f = await ecrireFuseau(fuseau);
  window.dispatchEvent(new Event(MOI_MODIFIE_EVENT));
  return f;
}

/**
 * Fuseau horaire du calendrier affiche : celui de l'athlete dont le coach
 * etablit le calendrier, sinon celui de l'utilisateur (a defaut, du navigateur).
 */
export function fuseauDuCalendrier(moi: Moi | null, athlete: AthleteRattache | null): string {
  return (athlete ? athlete.fuseau : moi?.fuseau) || fuseauNavigateur();
}

/** Utilisateur connecte (null tant qu'il n'est pas connu). */
export function useMoi(): Moi | null {
  const [moi, setMoi] = React.useState<Moi | null>(lireCache);
  React.useEffect(() => {
    const charger = () => chargerMoi().then(setMoi).catch(() => { /* role inconnu : acces restreint */ });
    charger();
    window.addEventListener(MOI_MODIFIE_EVENT, charger);
    return () => window.removeEventListener(MOI_MODIFIE_EVENT, charger);
  }, []);
  return moi;
}

/**
 * Profil choisi a la premiere connexion, une seule fois : le serveur refuse
 * ensuite tout changement (409).
 */
export async function choisirProfil(role: Role): Promise<Moi> {
  const moi = await lire<Moi>(
    await fetch('/api/moi/role', { method: 'POST', headers: entetes(), body: JSON.stringify({ role }) }),
  );
  ecrireCache(moi);
  window.dispatchEvent(new Event(MOI_MODIFIE_EVENT));
  return moi;
}

async function repondre(url: string, method: string): Promise<Moi> {
  const moi = await lire<Moi>(await fetch(url, { method, headers: entetes() }));
  ecrireCache(moi);
  window.dispatchEvent(new Event(MOI_MODIFIE_EVENT));
  return moi;
}

/** L'athlete accepte l'invitation d'un coach, qui etablira son calendrier. */
export function accepterInvitation(id: string): Promise<Moi> {
  return repondre('/api/moi/invitations/' + encodeURIComponent(id) + '/accepter', 'POST');
}

export function refuserInvitation(id: string): Promise<Moi> {
  return repondre('/api/moi/invitations/' + encodeURIComponent(id), 'DELETE');
}

/* Lien d'invitation d'un coach (LienInvitationService cote serveur) */

// Chemin du lien : /rejoindre/<jeton>.
export const CHEMIN_REJOINDRE = '/rejoindre/';

/** Jeton du lien d'invitation ouvert (adresse /rejoindre/<jeton>), sinon null. */
export function jetonInvitationDeLAdresse(): string | null {
  const chemin = window.location.pathname;
  if (!chemin.startsWith(CHEMIN_REJOINDRE)) return null;
  const jeton = decodeURIComponent(chemin.substring(CHEMIN_REJOINDRE.length)).replace(/\/+$/, '').trim();
  return jeton || null;
}

/** Adresse complete a transmettre pour un jeton. */
export function adresseInvitation(jeton: string): string {
  return window.location.origin + CHEMIN_REJOINDRE + encodeURIComponent(jeton);
}

export type LienInvitation = {
  // Coach : jeton de son lien (null s'il n'en a pas).
  jeton?: string | null;
  // Personne invitee : nom du coach et de son club (null pour un coach independant).
  coach?: string | null;
  club?: string | null;
};

/** Lien d'invitation actuel du coach. */
export async function chargerLienInvitation(): Promise<string | null> {
  const r = await lire<LienInvitation>(await fetch('/api/coach/lien-invitation', { headers: entetes() }));
  return r.jeton ?? null;
}

/** Nouveau lien d'invitation du coach : l'ancien ne fonctionne plus. */
export async function genererLienInvitation(): Promise<string> {
  const r = await lire<LienInvitation>(
    await fetch('/api/coach/lien-invitation', { method: 'POST', headers: entetes() }),
  );
  return r.jeton as string;
}

export async function desactiverLienInvitation(): Promise<void> {
  const r = await fetch('/api/coach/lien-invitation', { method: 'DELETE', headers: entetes() });
  if (r.status !== 204) await lire(r);
}

/** Coach et club d'un lien, lisibles avant la connexion ; null si le lien n'est plus valide. */
export async function lireInvitationPublique(jeton: string): Promise<LienInvitation | null> {
  const r = await fetch('/api/public/invitation/' + encodeURIComponent(jeton));
  if (r.status === 404) return null;
  return lire<LienInvitation>(r);
}

/** La personne connectee devient athlete du coach du lien (et membre de son club). */
export function rejoindreCoach(jeton: string): Promise<Moi> {
  return repondre('/api/moi/rejoindre/' + encodeURIComponent(jeton), 'POST');
}

/** L'athlete quitte son coach (son calendrier est conserve). */
export function quitterCoach(): Promise<Moi> {
  return repondre('/api/moi/coach', 'DELETE');
}

/* Suppression du compte (onglet Zone de danger) */

export type SuppressionCompte = {
  possible: boolean;
  // Ce qui empeche la suppression (athlete encore suivi, coach avec des athletes...).
  raison?: string | null;
};

// Compte supprime : Login.tsx revient a l'ecran de connexion.
export const COMPTE_SUPPRIME_EVENT = 'app:compte-supprime';

export async function controlerSuppressionCompte(): Promise<SuppressionCompte> {
  return lire<SuppressionCompte>(await fetch('/api/moi/suppression', { headers: entetes() }));
}

/** Supprime le compte et toutes ses donnees, puis deconnecte l'utilisateur. */
export async function supprimerCompte(): Promise<void> {
  const r = await fetch('/api/moi', { method: 'DELETE', headers: entetes() });
  if (r.status !== 204) await lire<unknown>(r);
  sessionStorage.removeItem(CLE_CACHE);
  window.dispatchEvent(new Event(COMPTE_SUPPRIME_EVENT));
}

/** Coach (definit les seances, modifie les calendriers). Inconnu = non. */
export function estCoach(moi: Moi | null): boolean {
  return moi?.role === 'COACH' && moi.administrateurSeul !== true;
}

/** Administrateur (comptes, courses communes). Inconnu = non. */
export function estAdmin(moi: Moi | null): boolean {
  return moi?.admin === true;
}

export function nomAthlete(a: AthleteRattache): string {
  return a.nom || a.email;
}

/* Athletes du coach */

export async function rattacherAthlete(email: string): Promise<AthleteRattache[]> {
  const liste = await lire<AthleteRattache[]>(
    await fetch('/api/coach/athletes', { method: 'POST', headers: entetes(), body: JSON.stringify({ email }) }),
  );
  window.dispatchEvent(new Event(MOI_MODIFIE_EVENT));
  return liste;
}

export async function detacherAthlete(id: string): Promise<AthleteRattache[]> {
  const liste = await lire<AthleteRattache[]>(
    await fetch('/api/coach/athletes/' + encodeURIComponent(id), { method: 'DELETE', headers: entetes() }),
  );
  window.dispatchEvent(new Event(MOI_MODIFIE_EVENT));
  return liste;
}

/** Le coach change le fuseau horaire d'un athlete rattache (jour courant de son calendrier). */
export async function enregistrerFuseauAthlete(ssoAthlete: string, fuseau: string): Promise<string> {
  const r = await lire<{ fuseau: string }>(
    await fetch('/api/coach/athletes/' + encodeURIComponent(ssoAthlete) + '/fuseau', {
      method: 'PUT',
      headers: entetes(),
      body: JSON.stringify({ fuseau }),
    }),
  );
  window.dispatchEvent(new Event(MOI_MODIFIE_EVENT));
  return r.fuseau;
}

/** Le coach fixe la distance de course preparee par un athlete rattache (null = aucune). */
export async function enregistrerDistancePreparee(ssoAthlete: string, distance: string | null): Promise<string | null> {
  const r = await lire<{ distance: string | null }>(
    await fetch('/api/coach/athletes/' + encodeURIComponent(ssoAthlete) + '/distance-preparee', {
      method: 'PUT',
      headers: entetes(),
      body: JSON.stringify({ distance }),
    }),
  );
  window.dispatchEvent(new Event(MOI_MODIFIE_EVENT));
  return r.distance ?? null;
}

export type ProfilAthlete = {
  vma: number | null;
  allures: { personnalisee: boolean; zones: unknown[] };
  ajustementRecup: number;
};

/** VMA, allures et ajustement des recuperations d'un athlete rattache. */
export async function lireProfilAthlete(ssoAthlete: string): Promise<ProfilAthlete> {
  return lire<ProfilAthlete>(
    await fetch('/api/coach/athletes/' + encodeURIComponent(ssoAthlete) + '/profil', { headers: entetes() }),
  );
}

/* Club de l'utilisateur (ClubController) : administrateurs, coachs qui
   partagent la bibliotheque de seances du club, et athletes suivis par un
   coach du club. */

export type MembreClub = {
  // Identifiant de l'adhesion.
  id: string;
  email: string;
  // Connus une fois que le membre s'est connecte.
  nom?: string | null;
  photo?: string | null;
  administrateur: boolean;
  coach: boolean;
  // Athlete du club. Ni coach ni athlete : administrateur seulement.
  athlete: boolean;
  // Compte au profil coach : un administrateur peut le faire entrainer.
  profilCoach: boolean;
  // Modifie la bibliotheque du club (coach).
  bibliothecaire: boolean;
  // Invitation de coach pas encore acceptee.
  enAttente: boolean;
  moi: boolean;
  // Coach : nombre d'athletes qu'il suit dans le club.
  nbAthletes: number;
  // Athlete : son coach (nom et adhesion), null s'il n'en a pas.
  coachSuivi?: string | null;
  idCoachSuivi?: string | null;
};

export type Club = {
  id: string;
  nom: string;
  // Club cree automatiquement pour un coach independant.
  personnel: boolean;
  // Droits de l'utilisateur connecte.
  administrateur: boolean;
  coach: boolean;
  athlete: boolean;
  bibliothecaire: boolean;
  nbSeancesBibliotheque: number;
  nbCoachs: number;
  // Administrateur : tout le club ; autres : eux-memes, les coachs et administrateurs.
  membres: MembreClub[];
};

// Invitation a rejoindre un club comme coach.
export type InvitationClub = {
  id: string;
  club?: string | null;
  dateCreation?: string | null;
};

// 204 : pas (ou plus) de club.
async function lireClub(r: Response): Promise<Club | null> {
  if (r.status === 204) return null;
  return lire<Club>(r);
}

async function modifierClub(url: string, method: string, corps?: unknown): Promise<Club | null> {
  const club = await lireClub(
    await fetch(url, { method, headers: entetes(), body: corps === undefined ? undefined : JSON.stringify(corps) }),
  );
  window.dispatchEvent(new Event(MOI_MODIFIE_EVENT));
  return club;
}

export async function chargerClub(): Promise<Club | null> {
  return lireClub(await fetch('/api/club', { headers: entetes() }));
}

export function renommerClub(nom: string): Promise<Club | null> {
  return modifierClub('/api/club', 'PUT', { nom });
}

/** Invite un coach (adresse Google) : il devient membre en acceptant. */
export function inviterCoachClub(email: string): Promise<Club | null> {
  return modifierClub('/api/club/coachs', 'POST', { email });
}

/** Roles d'un membre (administrateur, bibliothecaire ; absent = inchange). */
export function definirRolesMembre(
  idMembre: string,
  roles: { administrateur?: boolean; bibliothecaire?: boolean; coach?: boolean },
): Promise<Club | null> {
  return modifierClub('/api/club/membres/' + encodeURIComponent(idMembre), 'PUT', roles);
}

/** Confie un athlete du club a un autre coach du club (administrateur). */
export function changerCoachAthlete(idAthlete: string, idCoach: string): Promise<Club | null> {
  return modifierClub('/api/club/membres/' + encodeURIComponent(idAthlete) + '/coach', 'PUT', { coach: idCoach });
}

/** Retire un membre, annule une invitation, ou quitte le club (sa propre adhesion) : null s'il n'a plus de club. */
export function retirerMembreClub(idMembre: string): Promise<Club | null> {
  return modifierClub('/api/club/membres/' + encodeURIComponent(idMembre), 'DELETE');
}

/** Le coach accepte l'invitation d'un club : il quitte son club actuel avec ses athletes. */
export function accepterInvitationClub(id: string): Promise<Moi> {
  return repondre('/api/moi/invitations-club/' + encodeURIComponent(id) + '/accepter', 'POST');
}

export function refuserInvitationClub(id: string): Promise<Moi> {
  return repondre('/api/moi/invitations-club/' + encodeURIComponent(id), 'DELETE');
}

/** Administrateur du club de l'utilisateur (page Club). */
/** Administrateur de son club sans y entrainer (ni calendrier, ni journal, ni seances). */
export function estAdministrateurSeul(moi: Moi | null): boolean {
  return moi?.administrateurSeul === true;
}

export function estAdminClub(moi: Moi | null): boolean {
  return moi?.adminClub === true;
}

/** Page Club : coachs et administrateurs d'un club, pas les athletes (leur club et leur coach sont sur l'accueil). */
export function accesPageClub(moi: Moi | null): boolean {
  return !!moi?.idClub && (moi.role === 'COACH' || moi.adminClub === true);
}

/* Utilisateurs de l'application (reserve au coach) */

export type UtilisateurApp = {
  ssoUserId: string;
  // Nuls pour un compte connecte avant la memorisation de l'email.
  email?: string | null;
  nom?: string | null;
  // Null : profil pas encore choisi.
  role: Role | null;
  // Athlete : nom ou email de son coach (null s'il n'est rattache a aucun).
  coach?: string | null;
  // Club du compte (nom), null s'il n'en a pas.
  club?: string | null;
  // Coach : nombre d'athletes rattaches.
  nbAthletes: number;
  // Null si inconnue (connexion anterieure a son enregistrement).
  derniereConnexion?: string | null;
  dateCreation?: string | null;
};

export async function chargerUtilisateurs(): Promise<UtilisateurApp[]> {
  return lire<UtilisateurApp[]>(await fetch('/api/coach/utilisateurs', { headers: entetes() }));
}

/* Clubs de l'application (reserve a l'administrateur, cf runplan.admin.emails) */

export type ClubAdmin = {
  id: string;
  nom: string;
  // Club cree automatiquement pour un coach independant.
  personnel: boolean;
  dateCreation?: string | null;
  nbCoachs: number;
  nbAthletes: number;
  // Invitations de coachs en attente.
  nbInvitations: number;
  administrateurs: MembreClub[];
};

export async function chargerClubsAdmin(): Promise<ClubAdmin[]> {
  return lire<ClubAdmin[]>(await fetch('/api/admin/clubs', { headers: entetes() }));
}

/** Null : club inconnu (supprime entre-temps). */
export async function chargerClubAdmin(id: string): Promise<ClubAdmin | null> {
  const r = await fetch('/api/admin/clubs/' + encodeURIComponent(id), { headers: entetes() });
  if (r.status === 404) return null;
  return lire<ClubAdmin>(r);
}
